/**
 * Floating jobs dock, GDrive upload style.
 *
 * Bottom-right, fixed. Auto-shows when there is at least one active job
 * or a job that finished in the last 60 seconds. Minimisable to a
 * compact chip; user preference persisted in localStorage.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Activity, ChevronsRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/shared/cn';
import { useJobs } from '@/shared/jobs';
import JobItem from './JobItem';

const STORAGE_KEY = 'netra.jobs-dock.collapsed';
// How long a finished job lingers in the dock before fading out. 5 minutes
// matches GDrive — long enough to notice + click, short enough to clear.
const RECENT_WINDOW_MS = 5 * 60_000;

export default function JobsDock() {
  const { active, recent } = useJobs();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });
  const [dismissedUntil, setDismissedUntil] = useState<number>(0);
  const prevActiveCountRef = useRef<number>(0);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Auto-undismiss when a new active job arrives — dismiss only suppresses
  // post-completion lingering; new work must always surface.
  useEffect(() => {
    if (active.length > prevActiveCountRef.current && dismissedUntil > 0) {
      setDismissedUntil(0);
    }
    prevActiveCountRef.current = active.length;
  }, [active.length, dismissedUntil]);

  // Visible recent = finished within the last 60s, so the dock auto-fades
  // once everything has settled. Older finished jobs are still browsable
  // via the bell notifications + /jobs page.
  const visibleRecent = useMemo(() => {
    const cutoff = Date.now() - RECENT_WINDOW_MS;
    return recent.filter((j) => {
      const ts = j.completed_at ? new Date(j.completed_at).getTime() : 0;
      return ts >= cutoff;
    });
  }, [recent]);

  const hasAnything = active.length + visibleRecent.length > 0;
  // Dismiss only hides idle lingering recent items. Active jobs always
  // force the dock visible so users can monitor the submission they
  // just kicked off.
  const dismissedAndIdle = Date.now() < dismissedUntil && active.length === 0;

  if (!hasAnything || dismissedAndIdle) return null;

  const items = [...active, ...visibleRecent].slice(0, 6);
  const headline = active.length > 0
    ? `${active.length} berjalan${visibleRecent.length ? ` · ${visibleRecent.length} selesai` : ''}`
    : `${visibleRecent.length} selesai`;

  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-2rem)]',
        'bg-paper border-2 border-ink shadow-[0_8px_28px_-6px_rgba(0,0,0,0.35)]',
        'flex flex-col',
      )}
      role="region"
      aria-label="Jobs aktif"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b-2 border-ink bg-paper-2/60">
        <Activity className={cn('w-4 h-4 text-brass', active.length > 0 && 'animate-spin-slow')} />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-[13px] leading-tight uppercase tracking-wider">
            Jobs
          </div>
          <div className="font-mono text-[10px] text-ink-2 truncate">{headline}</div>
        </div>
        <button
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Buka' : 'Tutup'}
          className="grid place-items-center w-6 h-6 text-ink-2 hover:text-ink hover:bg-paper-2 transition"
        >
          {collapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={() => setDismissedUntil(Date.now() + 5 * 60 * 1000)}
          title="Sembunyikan 5 menit"
          className="grid place-items-center w-6 h-6 text-ink-3 hover:text-crimson hover:bg-paper-2 transition"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Body */}
      {!collapsed && (
        <>
          <div className="max-h-[400px] overflow-y-auto">
            {items.map((j) => (
              <JobItem key={j.id} job={j} compact />
            ))}
          </div>
          <div className="px-3 py-1.5 border-t border-ink-line bg-paper-2/40 text-right">
            <Link
              to="/jobs"
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-ink-2 hover:text-brass transition"
            >
              Semua jobs <ChevronsRight className="w-3 h-3" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
